import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Users, Key, Plus, Trash2, Copy, UserPlus, Eye, Shield, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TeamTab = ({ businessId, token }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'viewer' });
  const [keyName, setKeyName] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchTeam = async () => {
    if (!businessId) return;
    try {
      const res = await axios.get(`${API}/api/mybookie/team/${businessId}`, authHeaders);
      setTeamMembers(res.data);
    } catch (error) {
      console.error('Failed to load team:', error);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const res = await axios.get(`${API}/api/mybookie/api-keys`, authHeaders);
      setApiKeys(res.data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    }
  };

  useEffect(() => { fetchTeam(); fetchApiKeys(); }, [businessId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/mybookie/team/invite`, { ...inviteForm, business_id: businessId }, authHeaders);
      toast({ title: "Invitation sent", description: `Invited ${inviteForm.email} as ${inviteForm.role}` });
      setShowInvite(false);
      setInviteForm({ email: '', role: 'viewer' });
      fetchTeam();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleRemoveMember = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await axios.delete(`${API}/api/mybookie/team/${id}`, authHeaders);
      toast({ title: "Member removed" });
      fetchTeam();
    } catch (error) {
      console.error('Failed to remove member:', error);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/api-keys`, { name: keyName || 'Default Key' }, authHeaders);
      setNewKey(res.data.api_key);
      toast({ title: "API key created" });
      fetchApiKeys();
    } catch (err) {
      console.error("API key create error:", err);
      toast({ title: "Error", description: "Failed to create key", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleRevokeKey = async (id) => {
    if (!window.confirm('Revoke this API key?')) return;
    try {
      await axios.delete(`${API}/api/mybookie/api-keys/${id}`, authHeaders);
      toast({ title: "Key revoked" });
      fetchApiKeys();
    } catch (error) {
      console.error('Failed to revoke key:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!" });
  };

  const roleColors = {
    viewer: 'bg-blue-100 text-blue-800',
    editor: 'bg-green-100 text-green-800',
    admin: 'bg-purple-100 text-purple-800'
  };

  return (
    <div className="space-y-8" data-testid="team-tab">
      {/* Team Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-[#111614]">Team Members</h2>
            <p className="text-sm text-[#5C6661]">Manage who has access to this business</p>
          </div>
          <Button size="sm" onClick={() => setShowInvite(true)} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" data-testid="invite-member-btn">
            <UserPlus className="w-4 h-4 mr-1" /> Invite
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {teamMembers.length === 0 ? (
              <div className="text-center py-8 text-[#5C6661]">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>No team members yet. Invite someone to collaborate.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[#F6F5F2]">
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Role</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((m) => (
                    <tr key={m.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">{m.email}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${roleColors[m.role] || ''}`}>{m.role}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs ${m.status === 'accepted' ? 'text-green-600' : 'text-[#8A948F]'}`}>{m.status}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600" onClick={() => handleRemoveMember(m.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* API Keys Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-[#111614]">API Keys</h2>
            <p className="text-sm text-[#5C6661]">Manage programmatic access to your data</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => { setShowCreateKey(true); setNewKey(null); setKeyName(''); }} className="rounded-sm" data-testid="create-api-key-btn">
            <Key className="w-4 h-4 mr-1" /> Create Key
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {apiKeys.length === 0 ? (
              <div className="text-center py-8 text-[#5C6661]">
                <Key className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>No API keys yet. Create one to access the MyBookie API.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[#F6F5F2]">
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">Key</th>
                    <th className="text-left py-3 px-4">Created</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((k) => (
                    <tr key={k.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{k.name}</td>
                      <td className="py-3 px-4 font-mono text-xs text-[#5C6661]">{k.key_preview}</td>
                      <td className="py-3 px-4 text-[#5C6661]">{new Date(k.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600" onClick={() => handleRevokeKey(k.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Invite Team Member</DialogTitle></DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} required data-testid="invite-email" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                <SelectTrigger data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer"><div className="flex items-center gap-2"><Eye className="w-3 h-3" /> Viewer (read-only)</div></SelectItem>
                  <SelectItem value="editor"><div className="flex items-center gap-2"><Users className="w-3 h-3" /> Editor (add/edit)</div></SelectItem>
                  <SelectItem value="admin"><div className="flex items-center gap-2"><Shield className="w-3 h-3" /> Admin (full access)</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInvite(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" disabled={loading} data-testid="send-invite-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Invite'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateKey} onOpenChange={setShowCreateKey}>
        <DialogContent data-testid="create-key-dialog">
          <DialogHeader><DialogTitle>{newKey ? 'API Key Created' : 'Create API Key'}</DialogTitle></DialogHeader>
          {!newKey ? (
            <form onSubmit={handleCreateKey} className="space-y-4">
              <div>
                <Label>Key Name</Label>
                <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="e.g. Production, Testing" data-testid="key-name-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateKey(false)} className="rounded-sm">Cancel</Button>
                <Button type="submit" className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" disabled={loading} data-testid="generate-key-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Key'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-[#FFF8E1] border border-[#FFE082] rounded-sm p-3 text-sm text-[#5D4037]">
                Save this key now — it won't be shown again.
              </div>
              <div className="flex items-center gap-2 bg-[#F6F5F2] p-3 rounded-sm">
                <code className="text-xs font-mono flex-1 break-all" data-testid="new-api-key">{newKey}</code>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => copyToClipboard(newKey)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <Button onClick={() => setShowCreateKey(false)} className="w-full bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamTab;
